import type { Invoice } from '@/domains/invoices/types/models/Invoice.types'
import type {
  InvoicesDeletePaymentPayload,
  InvoicesDestroyPayload,
  InvoicesDownloadDeliveryNotePayload,
  InvoicesDownloadPayload,
  InvoicesGenerateDeliveryNotePayload,
  InvoicesGeneratePayload,
  InvoicesIndexPayload,
  InvoicesIssuePayload,
  InvoicesLineOptionsPayload,
  InvoicesRecordPaymentPayload,
  InvoicesSendPayload,
  InvoicesSettingsPayload,
  InvoicesShowPayload,
  InvoicesStorePayload,
  InvoicesUpdatePayload,
  InvoicesUpdatePaymentPayload,
  InvoicesVoidPayload
} from '@/domains/invoices/types/requests/EndpointRequest.types'
import type { EInvoiceReadinessResponse } from '@/domains/invoices/types/responses/EInvoiceReadinessResponse.types'
import type { InvoiceLineOptionsResponse } from '@/domains/invoices/types/responses/InvoiceLineOptionsResponse.types'
import type { InvoiceResponse } from '@/domains/invoices/types/responses/InvoiceResponse.types'
import type { InvoiceSettingsResponse } from '@/domains/invoices/types/responses/InvoiceSettingsResponse.types'
import type { MediaResponse } from '@/domains/media/types/responses/MediaResponse.types'
import type { PaginatedResponse } from '@/domains/shared/types/pagination/responses/PaginatedResponse.types'

import { twineServiceClient } from '@/services/api'
import { relativeZiggyRoute } from '@/utils/url-util/urlUtil'

/**
 * Invoices API Collection
 *
 * Provides methods for interacting with invoice endpoints.
 * Backend Controller: App\Admin\Http\Domains\Invoices\Controllers\InvoiceController
 *
 * @example
 * ```typescript
 * const { index, show, store, update, destroy } = callInvoices()
 *
 * // Fetch paginated invoices
 * const invoices = await index({ queryParams: { page: 1 } })
 *
 * // Create new invoice
 * const newInvoice = await store({
 *   body: createInvoiceRequest
 * })
 * ```
 */
const callInvoices = () => {
  /**
   * Fetch paginated list of invoices
   *
   * GET /admin/invoices
   *
   * @param payload - Query parameters for filtering/pagination
   * @returns Paginated invoice list
   */
  const index = (payload: InvoicesIndexPayload = {}) => {
    return twineServiceClient.get<PaginatedResponse<Invoice>>({
      endpoint: relativeZiggyRoute('admin.invoices.index'),
      queryParams: payload.queryParams,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Fetch single invoice by ID
   *
   * GET /admin/invoices/{invoice}
   *
   * @param payload - Invoice ID and options
   * @returns Invoice with nested order data
   */
  const show = (payload: InvoicesShowPayload) => {
    return twineServiceClient.get<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.show', { invoice: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Create new invoice
   *
   * POST /admin/invoices
   *
   * @param payload - Invoice creation data
   * @returns Created invoice
   */
  const store = (payload: InvoicesStorePayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.store'),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Update existing invoice
   *
   * PUT /admin/invoices/{invoice}
   *
   * @param payload - Invoice ID and update data
   * @returns Updated invoice
   */
  const update = (payload: InvoicesUpdatePayload) => {
    return twineServiceClient.put<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.update', { invoice: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Delete invoice
   *
   * DELETE /admin/invoices/{invoice}
   *
   * @param payload - Invoice ID
   * @returns void
   */
  const destroy = (payload: InvoicesDestroyPayload) => {
    return twineServiceClient.delete<void>({
      endpoint: relativeZiggyRoute('admin.invoices.destroy', { invoice: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Issue invoice (finalize draft)
   *
   * POST /admin/invoices/{invoice}/issue
   *
   * @param payload - Invoice ID
   * @returns Updated invoice with issued status
   */
  const issue = (payload: InvoicesIssuePayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.issue', { invoice: payload.id }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Void invoice
   *
   * POST /admin/invoices/{invoice}/void
   *
   * @param payload - Invoice ID
   * @returns Updated invoice with voided status
   */
  const voidInvoice = (payload: InvoicesVoidPayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.void', { invoice: payload.id }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Send invoice to customer
   *
   * POST /admin/invoices/{invoice}/send
   *
   * @param payload - Invoice ID and optional message
   * @returns Updated invoice
   */
  const send = (payload: InvoicesSendPayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.send', { invoice: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Download invoice PDF
   *
   * GET /admin/invoices/{invoice}/download
   *
   * @param payload - Invoice ID
   * @returns Media data with PDF URL
   */
  const download = (payload: InvoicesDownloadPayload) => {
    return twineServiceClient.get<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.download', { invoice: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Generate invoice PDF
   *
   * POST /admin/invoices/{invoice}/generate
   *
   * @param payload - Invoice ID
   * @returns Media data with generated PDF URL
   */
  const generate = (payload: InvoicesGeneratePayload) => {
    return twineServiceClient.post<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.generate', { invoice: payload.id }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Record payment for invoice
   *
   * POST /admin/invoices/{invoice}/pay
   *
   * @param payload - Invoice ID and payment data
   * @returns Updated invoice with payment recorded
   */
  const recordPayment = (payload: InvoicesRecordPaymentPayload) => {
    return twineServiceClient.post<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.pay', { invoice: payload.id }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Update payment for invoice
   *
   * PUT /admin/invoices/{invoice}/pay/{transactionId}
   *
   * @param payload - Invoice ID, transaction ID, and updated payment data
   * @returns Updated invoice
   */
  const updatePayment = (payload: InvoicesUpdatePaymentPayload) => {
    return twineServiceClient.put<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.update.payment', {
        invoice: payload.id,
        transactionId: payload.transactionId
      }),
      body: payload.body,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Delete payment for invoice
   *
   * DELETE /admin/invoices/{invoice}/pay/{transactionId}
   *
   * @param payload - Invoice ID and transaction ID
   * @returns Updated invoice with payment removed
   */
  const deletePayment = (payload: InvoicesDeletePaymentPayload) => {
    return twineServiceClient.delete<InvoiceResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.destroy.payment', {
        invoice: payload.id,
        transactionId: payload.transactionId
      }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Get invoice settings
   *
   * GET /admin/inventory/invoices/settings
   *
   * @param payload - Request options
   * @returns Invoice settings (bank details, terms)
   */
  const settings = (payload: InvoicesSettingsPayload = {}) => {
    return twineServiceClient.get<InvoiceSettingsResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.settings'),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Whether one location can send an e-invoice, and which seller details it still needs
   *
   * GET /admin/inventory/locations/{location}/e-invoicing
   *
   * @param locationId - The location to check
   * @returns Readiness with one row per requirement
   */
  const eInvoiceReadiness = (locationId: number) => {
    return twineServiceClient.get<EInvoiceReadinessResponse>({
      endpoint: relativeZiggyRoute('admin.inventory.locations.e-invoicing.show', { location: locationId })
    })
  }

  /**
   * Search what an invoice line can be added from
   *
   * GET /admin/finance/invoices/line-options
   *
   * @param payload - `q` to search, or `menu_item_ids` (comma-separated) to load menu items by id
   * @returns Product variants and menu items with their add-ons
   */
  const lineOptions = (payload: InvoicesLineOptionsPayload = {}) => {
    return twineServiceClient.get<InvoiceLineOptionsResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.line-options'),
      queryParams: payload.queryParams,
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Generate delivery note PDF for invoice
   *
   * POST /admin/invoices/{invoice}/delivery-note/generate
   *
   * @param payload - Invoice ID
   * @returns Media data with generated delivery note PDF URL
   */
  const generateDeliveryNote = (payload: InvoicesGenerateDeliveryNotePayload) => {
    return twineServiceClient.post<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.delivery-note.generate', { invoice: payload.id }),
      body: {},
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  /**
   * Download delivery note PDF for invoice
   *
   * GET /admin/invoices/{invoice}/delivery-note/download
   *
   * @param payload - Invoice ID
   * @returns Media data with delivery note PDF URL
   */
  const downloadDeliveryNote = (payload: InvoicesDownloadDeliveryNotePayload) => {
    return twineServiceClient.get<MediaResponse>({
      endpoint: relativeZiggyRoute('admin.invoices.delivery-note.download', { invoice: payload.id }),
      preventConcurrent: payload.preventConcurrent,
      token: payload.token
    })
  }

  return {
    index,
    show,
    store,
    update,
    destroy,
    issue,
    voidInvoice,
    send,
    download,
    generate,
    recordPayment,
    updatePayment,
    deletePayment,
    settings,
    eInvoiceReadiness,
    lineOptions,
    generateDeliveryNote,
    downloadDeliveryNote
  }
}

export { callInvoices }
